import { ScreenShell } from '@/components/layout/ScreenShell'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { GameButton } from '@/components/ui/GameButton'
import { ArtImage } from '@/components/ui/ArtImage'
import { useTutorial } from '@/contexts/TutorialProvider'
import { POWERUPS } from '@/game/catalog'
import { TILE_ART, powerupArt } from '@/game/art'
import type { PixelIconName } from '@/game/pixelIcons'

const TILE_LEGEND: { name: string; art: string; icon: PixelIconName; note: string }[] = [
  { name: 'Wall', art: TILE_ART.wall, icon: 'levelSelect', note: 'Stops a slide dead. Plan your bounces.' },
  { name: 'Home', art: TILE_ART.home, icon: 'home', note: 'The glowing portal. Reach it to win.' },
  { name: 'Coin', art: TILE_ART.coin, icon: 'coin', note: 'Scooped up as you slide across it.' },
  { name: 'Lava', art: TILE_ART.lava, icon: 'fire', note: 'Lethal. Sliding in ends the attempt.' },
  { name: 'Water', art: TILE_ART.water, icon: 'fire', note: 'Lethal. Keep Ember clear of it.' },
  { name: 'Ice', art: TILE_ART.ice, icon: 'speed', note: 'Keeps you sliding through, no stopping.' },
  { name: 'Breakable', art: TILE_ART.breakable, icon: 'star', note: 'Shatters when hit; stops you once, then is gone.' },
  { name: 'Key', art: TILE_ART.key, icon: 'hint', note: 'Grab it mid-slide to pass through a door.' },
  { name: 'Door', art: TILE_ART.door, icon: 'lock', note: 'Sealed until you carry a key or hit a switch.' },
  { name: 'Teleport', art: TILE_ART.teleport, icon: 'infinity', note: 'Hop to the paired pad and keep going.' },
  { name: 'Switch', art: TILE_ART.switch, icon: 'settings', note: 'Rolling over it opens every door at once.' },
]

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/40 p-4 flex flex-col gap-3">
      <h2 className="font-display font-bold text-[var(--text-dim)]">{title}</h2>
      {children}
    </div>
  )
}

function InfoRow({ icon, color, text }: { icon: PixelIconName; color: string; text: string }) {
  return (
    <div className="flex items-center gap-3 rounded-[var(--r-md)] bg-[var(--surface-raised)]/60 px-3 py-2.5">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--surface-raised)] shadow-emboss" style={{ color }}>
        <PixelIcon name={icon} size={18} />
      </span>
      <span className="text-sm font-semibold leading-tight text-[var(--text-dim)]">{text}</span>
    </div>
  )
}

const RULES: { icon: PixelIconName; color: string; text: string }[] = [
  { icon: 'home', color: 'var(--ember)', text: 'Reach the lit window to bring Ember home.' },
  { icon: 'steps', color: 'var(--text-dim)', text: 'Ember slides in a line until a wall stops it.' },
  { icon: 'star', color: 'var(--coin)', text: 'Fewer slides and faster times earn more stars.' },
]

const ENDLESS_FACTS: { icon: PixelIconName; color: string; text: string }[] = [
  { icon: 'infinity', color: 'var(--ember)', text: 'A fresh, solver-verified board every clear.' },
  { icon: 'lightning', color: 'var(--coin)', text: 'Difficulty climbs every five levels.' },
  { icon: 'trophy', color: 'var(--coin)', text: 'Streak ends when lives run out — score goes to Grace.' },
]

export function HelpScreen() {
  const { openTutorial } = useTutorial()
  return (
    <ScreenShell title="How to Play">
      <div className="flex flex-col gap-4">
        <div className="rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--ember)]/40 p-4 flex items-center gap-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-emboss text-[var(--ember)]">
            <PixelIcon name="book" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight">Interactive walkthrough</p>
            <p className="text-xs text-[var(--text-faint)] leading-tight">The animated how-to-play, step by step.</p>
          </div>
          <GameButton size="sm" icon="play" onClick={openTutorial}>
            Replay
          </GameButton>
        </div>

        <Section title="The Rules">
          <div className="flex flex-col gap-2">
            {RULES.map((r) => (
              <InfoRow key={r.text} icon={r.icon} color={r.color} text={r.text} />
            ))}
          </div>
        </Section>

        <Section title="Controls">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <InfoRow icon="mobile" color="var(--ember)" text="Swipe or use the corner joystick." />
            <InfoRow icon="gamepad" color="var(--text-dim)" text="WASD or arrows to slide." />
            <InfoRow icon="lightning" color="var(--coin)" text="Keys 1-0 fire powerups. Z undo, R restart." />
            <InfoRow icon="pause" color="var(--text-dim)" text="Esc or P pauses. Gamepads work too." />
          </div>
        </Section>

        <Section title="Tile Legend">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {TILE_LEGEND.map((t) => (
              <div key={t.name} className="flex items-center gap-2.5 rounded-[var(--r-md)] bg-[var(--surface-raised)]/60 p-2">
                <ArtImage
                  src={t.art}
                  alt={t.name}
                  fallbackIcon={t.icon}
                  fallbackColor="var(--text-dim)"
                  className="h-11 w-11 shrink-0 rounded-[var(--r-sm)]"
                  fit="cover"
                />
                <div className="min-w-0">
                  <span className="block text-sm font-bold leading-tight">{t.name}</span>
                  <span className="block text-[11px] leading-snug text-[var(--text-faint)]">{t.note}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Powerups">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {POWERUPS.map((p) => {
              const art = powerupArt(p.id)
              return (
                <div key={p.id} className="flex items-center gap-2.5 rounded-[var(--r-md)] bg-[var(--surface-raised)]/60 p-2">
                  {art ? (
                    <ArtImage
                      src={art}
                      alt={p.name}
                      fallbackIcon={p.icon}
                      fallbackColor="var(--ember)"
                      className="h-11 w-11 shrink-0 rounded-[var(--r-sm)]"
                      fit="cover"
                    />
                  ) : (
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--surface-raised)] shadow-emboss">
                      <PixelIcon name={p.icon} size={18} />
                    </span>
                  )}
                  <div className="min-w-0">
                    <span className="block text-sm font-bold leading-tight">{p.name}</span>
                    <span className="block text-[11px] leading-snug text-[var(--text-faint)]">{p.description}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </Section>

        <Section title="Endless Drift">
          <div className="flex flex-col gap-2">
            {ENDLESS_FACTS.map((r) => (
              <InfoRow key={r.text} icon={r.icon} color={r.color} text={r.text} />
            ))}
          </div>
        </Section>
      </div>
    </ScreenShell>
  )
}
